import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getVehicleHaltReport, HaltReportData, HaltReportVehicle, downloadVehicleHaltReportPDF, downloadVehicleHaltReportExcel, getTenant } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { getMaxDateToday } from '../../utils/dateUtils';
import HaltDetailDrawer from './HaltDetailDrawer';

const DEFAULT_CONFIG = {
  minHaltTime: 1200,
  maxSpeed: 2,
  minDistance: 5,
};

const PRESETS: { label: string; minHaltTime: number; maxSpeed: number; minDistance: number }[] = [
  { label: 'Long Halts (20min)', minHaltTime: 1200, maxSpeed: 2, minDistance: 5 },
  { label: 'Very Long Halts (40min)', minHaltTime: 2400, maxSpeed: 2, minDistance: 5 },
  { label: 'Extended Stops (1 Hr)', minHaltTime: 3600, maxSpeed: 2, minDistance: 5 },
  { label: 'Extended Stops (1.5 Hr)', minHaltTime: 5400, maxSpeed: 2, minDistance: 5 },
];


const HaltReport: React.FC = () => {
  const { user } = useAuth();
  const [reportData, setReportData] = useState<HaltReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedVehicles, setExpandedVehicles] = useState<Set<string>>(new Set());
  const [showConfig, setShowConfig] = useState(false);
  const [drawerVehicle, setDrawerVehicle] = useState<HaltReportVehicle | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string>('');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  // Tenant-sourced defaults (loaded once, then treated as the "reset" target)
  const [tenantDefaults, setTenantDefaults] = useState({ ...DEFAULT_CONFIG });

  // Date filter
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });

  // "Applied" config — the values that were last sent to the API
  const [appliedConfig, setAppliedConfig] = useState({ ...DEFAULT_CONFIG });

  // "Draft" config — what the user is editing before clicking Apply
  const [draftMinHaltTime, setDraftMinHaltTime] = useState(DEFAULT_CONFIG.minHaltTime);
  const [draftMaxSpeed, setDraftMaxSpeed] = useState(DEFAULT_CONFIG.maxSpeed);
  const [draftMinDistance, setDraftMinDistance] = useState(DEFAULT_CONFIG.minDistance);

  // Track whether the initial fetch has happened
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    const checkTheme = () => {
      setIsDarkTheme(document.documentElement.getAttribute('data-theme') === 'dark');
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Fetch tenant halt report config on mount
  useEffect(() => {
    const fetchTenantConfig = async () => {
      if (!user?.tenantId) {
        setConfigLoading(false);
        return;
      }
      try {
        const response = await getTenant(user.tenantId);
        if (response.data.success && response.data.data) {
          const cs = response.data.data.spec.configSettings;
          const cfg = {
            minHaltTime: cs?.haltReportMinHaltTimeSeconds ?? DEFAULT_CONFIG.minHaltTime,
            maxSpeed: cs?.haltReportMaxSpeedKmph ?? DEFAULT_CONFIG.maxSpeed,
            minDistance: cs?.haltReportMinDistanceMeters ?? DEFAULT_CONFIG.minDistance,
          };
          setTenantDefaults(cfg);
          setAppliedConfig(cfg);
          setDraftMinHaltTime(cfg.minHaltTime);
          setDraftMaxSpeed(cfg.maxSpeed);
          setDraftMinDistance(cfg.minDistance);
        }
      } catch (err) {
        // Silently fall back to hardcoded defaults
        console.error('Failed to load tenant halt config, using defaults:', err);
      } finally {
        setConfigLoading(false);
      }
    };
    fetchTenantConfig();
  }, [user?.tenantId]);

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };
    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadMenu]);

  const handleDownload = async (format: 'pdf' | 'excel') => {
    try {
      setDownloading(true);
      setDownloadError('');
      setShowDownloadMenu(false);

      const response = format === 'pdf'
        ? await downloadVehicleHaltReportPDF(selectedDate, appliedConfig.minHaltTime, appliedConfig.maxSpeed, appliedConfig.minDistance)
        : await downloadVehicleHaltReportExcel(selectedDate, appliedConfig.minHaltTime, appliedConfig.maxSpeed, appliedConfig.minDistance);

      const { url } = response.data.data;
      const link = document.createElement('a');
      link.href = url;
      link.download = `halt-report-${selectedDate}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error(`Error downloading ${format.toUpperCase()}:`, err);
      setDownloadError(err.response?.data?.message || `Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(false);
    }
  };

  const fetchHaltReport = useCallback(async (date: string, cfg: typeof DEFAULT_CONFIG) => {
    setLoading(true);
    setError('');
    try {
      const response = await getVehicleHaltReport(date, cfg.minHaltTime, cfg.maxSpeed, cfg.minDistance);
      setReportData(response.data.data);
    } catch (err: any) {
      console.error('Error fetching halt report:', err);
      setError(err.response?.data?.message || 'Failed to load halt report');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load — wait for tenant config to be ready
  useEffect(() => {
    if (!configLoading && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchHaltReport(selectedDate, appliedConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoading]);

  // Re-fetch when date changes (not config — config only on Apply)
  const prevDateRef = useRef(selectedDate);
  useEffect(() => {
    if (prevDateRef.current !== selectedDate) {
      prevDateRef.current = selectedDate;
      fetchHaltReport(selectedDate, appliedConfig);
    }
  }, [selectedDate, appliedConfig, fetchHaltReport]);

  const handleApplyFilters = () => {
    const newConfig = {
      minHaltTime: draftMinHaltTime,
      maxSpeed: draftMaxSpeed,
      minDistance: draftMinDistance,
    };
    setAppliedConfig(newConfig);
    fetchHaltReport(selectedDate, newConfig);
  };

  const handleResetToDefault = () => {
    setDraftMinHaltTime(tenantDefaults.minHaltTime);
    setDraftMaxSpeed(tenantDefaults.maxSpeed);
    setDraftMinDistance(tenantDefaults.minDistance);
  };

  const handlePreset = (preset: typeof PRESETS[0]) => {
    setDraftMinHaltTime(preset.minHaltTime);
    setDraftMaxSpeed(preset.maxSpeed);
    setDraftMinDistance(preset.minDistance);
  };

  const isPresetActive = (preset: typeof PRESETS[0]) =>
    draftMinHaltTime === preset.minHaltTime &&
    draftMaxSpeed === preset.maxSpeed &&
    draftMinDistance === preset.minDistance;

  const toggleVehicle = (vehicleNo: string) => {
    setExpandedVehicles((prev) => {
      const next = new Set(prev);
      if (next.has(vehicleNo)) {
        next.delete(vehicleNo);
      } else {
        next.add(vehicleNo);
      }
      return next;
    });
  };

  const expandAll = () => {
    if (!reportData) return;
    setExpandedVehicles(new Set(reportData.reports.map((r) => r.vehicleNo)));
  };

  const collapseAll = () => {
    setExpandedVehicles(new Set());
  };

  const filteredReports: HaltReportVehicle[] = (reportData?.reports || []).filter((item) => {
    if (!searchTerm.trim()) return true;
    const s = searchTerm.toLowerCase();
    return (
      item.vehicleNo?.toLowerCase().includes(s) ||
      item.licensePlate?.toLowerCase().includes(s)
    );
  });

  const formatTime = (isoString: string): string => {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '—';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  };

  const getGoogleMapsLink = (lat: number, lng: number): string => {
    return `https://www.google.com/maps?q=${lat},${lng}`;
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '14px',
    border: isDarkTheme ? '1px solid #475569' : '1px solid #cbd5e1',
    backgroundColor: isDarkTheme ? '#0f172a' : '#ffffff',
    color: isDarkTheme ? '#e2e8f0' : '#1e293b',
    width: '120px',
    height: '38px',
    boxSizing: 'border-box' as const,
  };

  const fieldLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 500,
    color: isDarkTheme ? '#94a3b8' : '#64748b',
    marginBottom: '4px',
  };

  if (loading) {
    return (
      <div>
        <div className="loading">Loading halt report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="empty-state">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Top bar: summary badges + search + date + config toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{
            padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 600,
            backgroundColor: isDarkTheme ? '#3b1f1f' : '#fee2e2',
            color: isDarkTheme ? '#fca5a5' : '#991b1b',
          }}>
            Total Halts: {reportData?.totalHalts || 0}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap' }}>
          <input
            type="text"
            placeholder="Search vehicle, plate..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{
              padding: '8px 12px', borderRadius: '6px', fontSize: '14px', width: '200px',
              border: '1px solid', borderColor: isDarkTheme ? '#334155' : '#ddd',
              height: '36px', boxSizing: 'border-box',
            }}
          />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            max={getMaxDateToday()}
            className="reports-date-input"
            style={{ padding: '8px 12px', borderRadius: '6px', fontSize: '14px', height: '36px', boxSizing: 'border-box' }}
          />
          <button
            onClick={() => setShowConfig(!showConfig)}
            style={{
              padding: '8px 14px',
              backgroundColor: showConfig
                ? (isDarkTheme ? '#3b82f6' : '#3b82f6')
                : (isDarkTheme ? '#334155' : '#f1f5f9'),
              color: showConfig ? '#ffffff' : (isDarkTheme ? '#e2e8f0' : '#475569'),
              border: showConfig ? '1px solid #3b82f6' : (isDarkTheme ? '1px solid #475569' : '1px solid #cbd5e1'),
              borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500,
              height: '36px', boxSizing: 'border-box', whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', gap: '4px',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
              <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
              <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
              <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
              <line x1="17" y1="16" x2="23" y2="16" />
            </svg>
            Filters
          </button>
          <div style={{ position: 'relative' }} ref={downloadMenuRef}>
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              disabled={loading || downloading || !reportData?.reports?.length}
              style={{
                padding: '8px 14px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: (loading || downloading || !reportData?.reports?.length) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                opacity: (loading || downloading || !reportData?.reports?.length) ? 0.6 : 1,
                height: '36px',
                boxSizing: 'border-box' as const,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
              }}
            >
              {downloading ? 'Generating...' : 'Download'}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
            {showDownloadMenu && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '5px',
                backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                border: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                borderRadius: '6px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', zIndex: 1000, minWidth: '180px',
              }}>
                <button onClick={() => handleDownload('pdf')} disabled={downloading}
                  style={{ width: '100%', padding: '10px 15px', backgroundColor: 'transparent', color: isDarkTheme ? '#ffffff' : '#333', border: 'none', borderRadius: '6px 6px 0 0', cursor: downloading ? 'not-allowed' : 'pointer', fontSize: '14px', textAlign: 'left', opacity: downloading ? 0.6 : 1 }}
                  onMouseEnter={(e) => { if (!downloading) e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >Download as PDF</button>
                <button onClick={() => handleDownload('excel')} disabled={downloading}
                  style={{ width: '100%', padding: '10px 15px', backgroundColor: 'transparent', color: isDarkTheme ? '#ffffff' : '#333', border: 'none', borderTop: isDarkTheme ? '1px solid #334155' : '1px solid #ddd', borderRadius: '0 0 6px 6px', cursor: downloading ? 'not-allowed' : 'pointer', fontSize: '14px', textAlign: 'left', opacity: downloading ? 0.6 : 1 }}
                  onMouseEnter={(e) => { if (!downloading) e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >Download as Excel</button>
              </div>
            )}
          </div>
        </div>
      </div>
      {downloadError && (
        <div style={{ padding: '8px 12px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c33', fontSize: '14px', marginBottom: '10px' }}>
          {downloadError}
        </div>
      )}

      {/* Filter Configuration panel */}
      {showConfig && (
        <div style={{
          padding: '16px 20px',
          marginBottom: '14px',
          borderRadius: '8px',
          backgroundColor: isDarkTheme ? '#1e293b' : '#f8fafc',
          border: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
        }}>
          {/* Row 1: Title + Quick Presets */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 600, fontSize: '14px', color: isDarkTheme ? '#e2e8f0' : '#1e293b', whiteSpace: 'nowrap' }}>
              Filter Configuration
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: isDarkTheme ? '#94a3b8' : '#64748b' }}>Presets:</span>
              {PRESETS.map((preset) => {
                const active = isPresetActive(preset);
                return (
                  <button
                    key={preset.label}
                    onClick={() => handlePreset(preset)}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      border: active
                        ? (isDarkTheme ? '1.5px solid #60a5fa' : '1.5px solid #3b82f6')
                        : (isDarkTheme ? '1px solid #475569' : '1px solid #cbd5e1'),
                      backgroundColor: active
                        ? (isDarkTheme ? '#1e3a5f' : '#eff6ff')
                        : 'transparent',
                      color: active
                        ? (isDarkTheme ? '#93c5fd' : '#2563eb')
                        : (isDarkTheme ? '#cbd5e1' : '#475569'),
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Row 2: Inputs + Actions all in one row */}
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <div style={fieldLabelStyle}>Min Halt Time (seconds)</div>
              <input
                type="number"
                value={draftMinHaltTime}
                onChange={(e) => setDraftMinHaltTime(parseInt(e.target.value) || 0)}
                min={1}
                style={inputStyle}
              />
            </div>
            <div>
              <div style={fieldLabelStyle}>Max Speed (km/h)</div>
              <input
                type="number"
                value={draftMaxSpeed}
                onChange={(e) => setDraftMaxSpeed(parseInt(e.target.value) || 0)}
                min={1}
                style={inputStyle}
              />
            </div>
            <div>
              <div style={fieldLabelStyle}>Min Distance (meters)</div>
              <input
                type="number"
                value={draftMinDistance}
                onChange={(e) => setDraftMinDistance(parseInt(e.target.value) || 0)}
                min={1}
                style={inputStyle}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <button
                onClick={handleApplyFilters}
                disabled={loading}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#4f46e5',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  opacity: loading ? 0.6 : 1,
                  height: '38px',
                  boxSizing: 'border-box' as const,
                }}
              >
                Apply Filters
              </button>
              <button
                onClick={handleResetToDefault}
                style={{
                  padding: '8px 20px',
                  backgroundColor: 'transparent',
                  color: isDarkTheme ? '#cbd5e1' : '#475569',
                  border: isDarkTheme ? '1px solid #475569' : '1px solid #cbd5e1',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  height: '38px',
                  boxSizing: 'border-box' as const,
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expand/Collapse controls */}
      {filteredReports.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
          <button
            onClick={expandAll}
            style={{
              padding: '4px 12px', fontSize: '12px', border: 'none', borderRadius: '4px', cursor: 'pointer',
              backgroundColor: isDarkTheme ? '#334155' : '#e2e8f0',
              color: isDarkTheme ? '#e2e8f0' : '#475569',
            }}
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            style={{
              padding: '4px 12px', fontSize: '12px', border: 'none', borderRadius: '4px', cursor: 'pointer',
              backgroundColor: isDarkTheme ? '#334155' : '#e2e8f0',
              color: isDarkTheme ? '#e2e8f0' : '#475569',
            }}
          >
            Collapse All
          </button>
        </div>
      )}

      {/* Results */}
      {filteredReports.length === 0 ? (
        <div className="empty-state">
          <h3>{(reportData?.reports?.length || 0) === 0 ? 'No halts detected' : 'No matching results'}</h3>
          <p>{(reportData?.reports?.length || 0) === 0 ? 'No vehicle halts were detected for this date and configuration' : 'Try adjusting your search criteria'}</p>
        </div>
      ) : (
        <div
          className="table-container"
          style={{
            height: showConfig ? 'calc(100vh - 560px)' : 'calc(100vh - 360px)',
            overflowY: 'auto',
            overflowX: 'auto',
            border: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
            backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
          }}
        >
          <table
            className="bookings-table"
            style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: '900px' }}
          >
            <thead>
              <tr>
                {['#', 'Vehicle', 'License Plate', 'Total Halts', 'Total Halt Duration', ''].map((header) => (
                  <th
                    key={header}
                    style={{
                      padding: '10px 12px',
                      textAlign: 'left',
                      fontWeight: '600',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: isDarkTheme ? '#94a3b8' : '#64748b',
                      backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                      borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredReports.map((vehicle, rowIdx) => {
                const isExpanded = expandedVehicles.has(vehicle.vehicleNo);
                const baseBgColor = rowIdx % 2 === 0
                  ? (isDarkTheme ? '#1e293b' : '#ffffff')
                  : (isDarkTheme ? '#0f172a' : '#f8fafc');
                const hoverBgColor = isDarkTheme ? '#334155' : '#f1f5f9';

                return (
                  <React.Fragment key={vehicle.vehicleNo}>
                    <tr
                      style={{ backgroundColor: baseBgColor, cursor: 'pointer', transition: 'background-color 0.15s ease' }}
                      onClick={() => toggleVehicle(vehicle.vehicleNo)}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBgColor; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = baseBgColor; }}
                    >
                      <td style={{
                        padding: '10px 12px',
                        borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                        color: isDarkTheme ? '#94a3b8' : '#64748b',
                        fontSize: '14px', width: '40px',
                      }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <svg
                            width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
                          >
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                          {vehicle.slNo}
                        </span>
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                        color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                        fontWeight: '600', fontSize: '14px',
                      }}>
                        {vehicle.vehicleNo}
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                        color: isDarkTheme ? '#cbd5e1' : '#475569', fontSize: '14px',
                      }}>
                        {vehicle.licensePlate || '—'}
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                        fontSize: '14px',
                      }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: '12px', fontWeight: 600, fontSize: '13px',
                          backgroundColor: isDarkTheme ? '#3b1f1f' : '#fee2e2',
                          color: isDarkTheme ? '#fca5a5' : '#991b1b',
                        }}>
                          {vehicle.totalHalts}
                        </span>
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                        color: isDarkTheme ? '#fbbf24' : '#92400e',
                        fontWeight: 500, fontSize: '14px',
                      }}>
                        {vehicle.totalHaltDurationFormatted}
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                        textAlign: 'center',
                        width: '80px',
                      }}>
                        {vehicle.totalHalts > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDrawerVehicle(vehicle);
                            }}
                            style={{
                              padding: '5px 14px',
                              backgroundColor: isDarkTheme ? '#1e3a5f' : '#eff6ff',
                              color: isDarkTheme ? '#60a5fa' : '#2563eb',
                              border: isDarkTheme ? '1px solid #2563eb' : '1px solid #93c5fd',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = isDarkTheme ? '#2563eb' : '#dbeafe';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isDarkTheme ? '#1e3a5f' : '#eff6ff';
                            }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                            View
                          </button>
                        )}
                      </td>
                    </tr>

                    {isExpanded && vehicle.haltDetails.map((halt, haltIdx) => (
                      <tr
                        key={`${vehicle.vehicleNo}-halt-${haltIdx}`}
                        style={{ backgroundColor: isDarkTheme ? '#0c1524' : '#f0f9ff' }}
                      >
                        <td style={{
                          padding: '8px 12px',
                          borderBottom: isDarkTheme ? '1px solid #1e293b' : '1px solid #e0f2fe',
                          fontSize: '13px', color: isDarkTheme ? '#475569' : '#94a3b8',
                          textAlign: 'right', paddingRight: '16px',
                        }}>
                          {haltIdx + 1}
                        </td>
                        <td style={{
                          padding: '8px 12px',
                          borderBottom: isDarkTheme ? '1px solid #1e293b' : '1px solid #e0f2fe',
                          fontSize: '13px',
                        }}>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                            <div>
                              <span style={{ color: isDarkTheme ? '#64748b' : '#94a3b8', marginRight: '4px' }}>Start:</span>
                              <span style={{ color: isDarkTheme ? '#e2e8f0' : '#334155', fontWeight: 500 }}>
                                {formatTime(halt.startTime)}
                              </span>
                            </div>
                            <div>
                              <span style={{ color: isDarkTheme ? '#64748b' : '#94a3b8', marginRight: '4px' }}>End:</span>
                              <span style={{ color: isDarkTheme ? '#e2e8f0' : '#334155', fontWeight: 500 }}>
                                {formatTime(halt.endTime)}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td style={{
                          padding: '8px 12px',
                          borderBottom: isDarkTheme ? '1px solid #1e293b' : '1px solid #e0f2fe',
                          fontSize: '13px',
                        }}>
                          <span style={{ fontWeight: 600, color: isDarkTheme ? '#fbbf24' : '#92400e' }}>
                            {halt.durationFormatted}
                          </span>
                        </td>
                        <td colSpan={3} style={{
                          padding: '8px 12px',
                          borderBottom: isDarkTheme ? '1px solid #1e293b' : '1px solid #e0f2fe',
                          fontSize: '13px',
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {halt.address && halt.address !== '—' && (
                              <span style={{ color: isDarkTheme ? '#e2e8f0' : '#334155', fontWeight: 500, fontSize: '12px' }}>
                                {halt.address}
                              </span>
                            )}
                            <a
                              href={getGoogleMapsLink(halt.location.lat, halt.location.lng)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: isDarkTheme ? '#60a5fa' : '#2563eb',
                                textDecoration: 'none', fontWeight: 500, fontSize: '12px',
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                <circle cx="12" cy="10" r="3" />
                              </svg>
                              {halt.location.lat.toFixed(4)}, {halt.location.lng.toFixed(4)}
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Halt Detail Drawer */}
      <HaltDetailDrawer
        open={drawerVehicle !== null}
        onClose={() => setDrawerVehicle(null)}
        vehicleNo={drawerVehicle?.vehicleNo || ''}
        licensePlate={drawerVehicle?.licensePlate || null}
        deviceImei={drawerVehicle?.deviceImei || null}
        haltDetails={drawerVehicle?.haltDetails || []}
        selectedDate={selectedDate}
        isDarkTheme={isDarkTheme}
      />
    </div>
  );
};

export default HaltReport;
